import React, { useState, useEffect, useRef, useContext } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import maharashtraGeo from "../../locales/maharashtra_districts_mr.json";
import { adminService } from "../../_services/admin.service";
import { Modal } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import { LanguageContext } from "../../context/LanguageContext";

const AnimatedCount = ({ value, duration = 900 }) => {
  const [display, setDisplay] = useState(0);
  const previous = useRef(0);
  const raf = useRef(null);

  useEffect(() => {
    const startTime = performance.now();
    const startValue = previous.current;

    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const animate = (time) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOut(progress);

      const current = Math.round(startValue + (value - startValue) * eased);

      setDisplay(current);

      if (progress < 1) {
        raf.current = requestAnimationFrame(animate);
      } else {
        previous.current = value;
      }
    };

    raf.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(raf.current);
  }, [value, duration]);

  return <span>{display.toLocaleString()}</span>;
};

const MaharashtraMap = ({ independenceTheme = false }) => {
  const [activeCity, setActiveCity] = useState([]);
  const [count, setCount] = useState(0);
  const [selectCityId, setSelectCityId] = useState();
  const [mappedCity, setMappedCity] = useState();
  const [isMappedCity, setIsMappedCity] = useState(false);
  const [selectCityCount, setSelectCityCount] = useState();
  const { language, setLanguage, t } = useContext(LanguageContext);

  const handleMappedCity = (district) => {
    setIsMappedCity(true);
  };

  const handleMappedCloseCity = () => {
    setIsMappedCity(false);
  };

  const selectCity = (id) => {
    setSelectCityCount(id);
    handleMappedCloseCity();
    const temp = {
      cityName: id,
    };
    adminService.countCity(temp).then((res) => {
      console.log("res", res[0]?.crimeCount);
      if (res[0]?.crimeCount) {
        setCount(res[0]?.crimeCount);
      } else {
        setCount(0);
      }
    });
  };

  const TotalCrimeCount = () => {
    adminService.TotalCrimeCount().then((res) => {
      console.log("res", res[0]?.count);
      if (res[0]?.count) {
        setCount(res[0]?.count);
      } else {
        setCount(0);
      }
    });
  };
  const mapCityList = (district) => {
    setSelectCityId(district);

    const temp = {
      id: district,
    };

    adminService.mapCityList(temp).then((res) => {
      if (res[0]?.subCities?.length) {
        setMappedCity(res[0].subCities);
        handleMappedCity();
      } else {
        selectCity(district); // ✅ use district directly
      }
    });
  };

  const activeCityList = () => {
    adminService.activeCityList().then((res) => {
      setActiveCity(res || []);
    });
  };

  useEffect(() => {
    activeCityList();
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      TotalCrimeCount();
    }, 1200);

    return () => clearTimeout(timer); // cleanup
  }, []);

  // 🔹 helper to check active district
  const isActiveCity = (district) =>
    activeCity.some(
      (c) => c.city.trim().toLowerCase() === district.trim().toLowerCase()
    );

  console.log("select city", selectCityId);

  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = width <= 768;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        marginLeft: isMobile ? 0 : "-100px",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100vh",
        }}
      >
        {/* Map Section */}
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              center: [76.2, 19.2],
              scale: isMobile ? 4800 : 5000,
            }}
            style={{ width: "100%", height: "100%" }}
          >
            <Geographies geography={maharashtraGeo}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const district = geo.properties.district;
                  const centroid = geoCentroid(geo);
                  const isActive = isActiveCity(district);

                  return (
                    <g key={district}>
                      {/* District Shape */}
                      <Geography
                        geography={geo}
                        className={isActive ? "district-active" : "district"}
                        fill={isActive ? "#e6f4ff" : "#eef4ff"}                        
                        stroke={isActive ? "#1677ff" : "#6a0dad"}
                        strokeWidth={isMobile ? 0.5 : isActive ? 2.2 : 0.7}
                        style={{
                          default: { outline: "none" },
                          hover: {
                            fill: isActive ? "#bae0ff" : "#ffd699",
                            outline: "none",
                          },
                        }}
                        onClick={() => {
                          mapCityList(district);
                        }}
                      />

                      {/* District Label */}
                      <Marker coordinates={centroid}>
                        <text
                          textAnchor="middle"
                          alignmentBaseline="middle"
                          style={{
                            fontSize: isMobile ? "8px" : "14px",
                            fill: isActive ? "#ff4d4f" : "#2c2c2c",
                            fontWeight: isActive ? 700 : 500,
                            pointerEvents: "none",
                          }}
                        >
                          {district}
                        </text>
                      </Marker>
                    </g>
                  );
                })
              }
            </Geographies>
          </ComposableMap>
        </div>

        {/* Right Side Card */}
        {selectCityCount && count ? (
          <div className="district-card">
            <div className="district-card-inner">
              {independenceTheme && (
                <div className="ind-day-stat-icon" aria-hidden="true">
                  <SafetyCertificateOutlined />
                </div>
              )}
              <div className={independenceTheme ? "ind-day-stat-body" : undefined}>
                <p>
                  <strong>घटक:</strong> {selectCityCount}
                </p>

                <p>
                  <strong>हद्दीत राहणारे आरोपी:</strong>{" "}
                  <span className="count">
                    <AnimatedCount value={count} />
                  </span>
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="district-card">
            <div className="district-card-inner">
              {independenceTheme && (
                <div className="ind-day-stat-icon" aria-hidden="true">
                  <SafetyCertificateOutlined />
                </div>
              )}
              <div className={independenceTheme ? "ind-day-stat-body" : undefined}>
                <p>
                  {independenceTheme ? (
                    <span className="ind-day-stat-label">एकूण आरोपी:</span>
                  ) : (
                    <strong> एकूण आरोपी:</strong>
                  )}{" "}
                  <span className="count">
                    <AnimatedCount value={count} />
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {isMappedCity && mappedCity?.length > 0 && (
        <div className="city-overlay">
          <div
            onClick={handleMappedCloseCity}
            style={{
              position: "absolute",
              top: "20px",
              right: "30px",
              fontSize: "28px",
              color: "#fff",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            ×
          </div>

          <div className="city-options">
            {mappedCity.map((city, index) => (
              <div
                key={index}
                style={{
                  background: "#fff",
                  padding: "10px 24px",
                  borderRadius: "4px",
                  border: "1px solid #4b2e83",
                  color: "#4b2e83",
                  cursor: "pointer",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f5f0ff")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#fff")
                }
                onClick={() => selectCity(city)} // move selectCity here
              >
                <p style={{ marginBottom: "0px" }}>{city}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MaharashtraMap;
